import * as React from 'react'
import { cn } from '../../lib/utils'

export interface BannerButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	title: string
	subtitle?: string
	icon?: React.ReactNode
	image?: string
	editIcon?: React.ReactNode
	onEdit?: (e: React.MouseEvent) => void
}

const BannerButton = React.forwardRef<HTMLButtonElement, BannerButtonProps>(
	({ className, title, subtitle, icon, image, editIcon, onEdit, ...props }, ref) => {
		if (image) {
			return (
				// Wrap in div to avoid nested <button> — a11y violation
				<div className="relative w-[205px] h-[116px]">
					<button
						ref={ref}
						className={cn(
							'absolute inset-0 w-full h-full rounded-xl overflow-hidden cursor-pointer',
							'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2',
							'disabled:opacity-50 disabled:pointer-events-none',
							className
						)}
						{...props}
					>
						<img src={image} alt={title} className="size-full object-cover" />
					</button>
					{onEdit && (
						<button
							type="button"
							onClick={(e) => { e.stopPropagation(); onEdit(e) }}
							className="absolute top-2.5 right-2.5 bg-(--color-secondary) text-(--color-secondary-foreground) p-1.5 rounded-md hover:bg-(--color-secondary-hover) transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring)"
							aria-label="Edit"
						>
							{editIcon ?? (
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
								</svg>
							)}
						</button>
					)}
				</div>
			)
		}

		return (
			<button
				ref={ref}
				className={cn(
					'w-[205px] flex flex-col items-center justify-center gap-1 px-4 py-6 rounded-xl',
					'bg-(--color-brand-light) hover:bg-(--color-brand-light-hover) transition-colors duration-150',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2',
					'disabled:opacity-50 disabled:pointer-events-none cursor-pointer',
					className
				)}
				{...props}
			>
				{icon && (
					<span className="size-5 text-(--color-brand) flex items-center justify-center">
						{icon}
					</span>
				)}
				<span className="text-sm font-[500] leading-6 text-(--color-brand) text-center w-full">
					{title}
				</span>
				{subtitle && (
					// Use foreground instead of muted-foreground for better contrast on brand-light bg
					<span className="text-xs leading-4 text-(--color-primary) opacity-70 text-center">
						{subtitle}
					</span>
				)}
			</button>
		)
	}
)

BannerButton.displayName = 'BannerButton'

export { BannerButton }
